import Modal from "react-bootstrap/Modal";

import Header from "./components/Header";
import Content from "./components/Content";
import Footer from "./components/Footer";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    postCreateAuthService,
    postTestAuthService,
    getAuthServiceDetail,
    putUpdateAuthService,
} from "helpers/leads/stepHelpers/sendStep1ToServis";
import SwalAlertComp from "components/SwalAlert";

const CreateAuthServiceModal = ({
    onClose,
    onCreateSuccess,
    authServiceDetails = null,
    isEditMode = false,
}) => {
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [isLoadingTest, setIsLoadingTest] = useState(false);
    const [testResult, setTestResult] = useState(
        authServiceDetails?.test_result || {},
    );
    const [isAuthHeaderChecked, setIsAuthHeaderChecked] = useState(
        !!(
            authServiceDetails?.auth_headers?.key ||
            authServiceDetails?.auth_headers?.type ||
            authServiceDetails?.auth_headers?.values?.length
        ),
    );
    const [isBodyChecked, setIsBodyChecked] = useState(
        !!(
            authServiceDetails?.body_type ||
            (authServiceDetails?.body &&
                Array.isArray(authServiceDetails.body) &&
                authServiceDetails.body.length > 0)
        ),
    );
    const [isLoadingData, setIsLoadingData] = useState(false);

    const [touchedAuthValueIndices, setTouchedAuthValueIndices] = useState(
        new Set(),
    );

    const originalAuthValues = useRef(
        authServiceDetails?.auth_headers?.values || [],
    );

    // Helper function to convert body array to the expected format
    const getBodyData = () => {
        if (!authServiceDetails?.body) {
            return [{ field: "", value: "" }];
        }

        const bodyType = authServiceDetails?.body_type?.toUpperCase();

        // If body_type is JSON, return the body as string
        if (bodyType === "JSON") {
            return typeof authServiceDetails.body === "string"
                ? authServiceDetails.body
                : JSON.stringify(authServiceDetails.body, null, 2);
        }

        // If body_type is FORM_PARAMS or body is an array of objects
        if (Array.isArray(authServiceDetails.body)) {
            if (authServiceDetails.body.length === 0) {
                return [{ field: "", value: "" }];
            }

            // If body is already in array of {field, value} format
            if (authServiceDetails.body[0]?.field !== undefined) {
                return authServiceDetails.body;
            }

            // If body is an object with key-value pairs, convert to array format
            return Object.entries(authServiceDetails.body[0] || {}).map(
                ([field, value]) => ({
                    field,
                    value: value || "",
                }),
            );
        }

        // If body is an object, convert to array format
        if (typeof authServiceDetails.body === "object") {
            return Object.entries(authServiceDetails.body).map(
                ([field, value]) => ({
                    field,
                    value: value || "",
                }),
            );
        }

        return [{ field: "", value: "" }];
    };

    const hookForm = useForm({
        mode: "onBlur",
        defaultValues: {
            name: authServiceDetails?.name || null,
            url: authServiceDetails?.request_url || null,
            method: authServiceDetails?.request_method || null,
            auth_header: {
                key: authServiceDetails?.auth_headers?.key || null,
                type:
                    authServiceDetails?.auth_headers?.type
                        ?.toString()
                        ?.toUpperCase() || null,
                values: Array.isArray(authServiceDetails?.auth_headers?.values)
                    ? authServiceDetails.auth_headers.values.map((item) => ({
                          field: item || null,
                      }))
                    : [],
            },
            body: {
                type: authServiceDetails?.body_type?.toUpperCase() || null,
                data: getBodyData(),
            },
            expected_response: {
                token_key: authServiceDetails?.expected_token_key || null,
                token_expiry_rule:
                    authServiceDetails?.token_expiration_rule || null,
                token_expiry_time: authServiceDetails?.expires_in || null,
                token_expiry_key:
                    authServiceDetails?.token_expiration_key || null,
            },
        },
    });

    const handleSubmitErrors = (error) => {
        if (
            error.message === "Unexpected end of JSON input" ||
            error.message ===
                "TypeError: _data$body4$data.reduce is not a function"
        )
            SwalAlertComp({
                title: "Error",
                message: "Please input valid JSON.",
                icon: "error",
            });
        else
            SwalAlertComp({
                title: "Error",
                message: "Please fill the form correctly.",
                icon: "error",
            });
    };

    const handleAuthValueChange = useCallback((index) => {
        setTouchedAuthValueIndices((prev) => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
        });
    }, []);

    const handleResetTouchedIndices = useCallback(() => {
        setTouchedAuthValueIndices(new Set());
    }, []);

    const onSubmitHandler = useCallback(
        async (data) => {
            setIsLoadingSubmit(true);
            const auth_header = data?.auth_header;
            const body = data?.body;

            try {
                const bodyData = Array.isArray(body?.data)
                    ? data?.body?.data
                          ?.filter((item) => item?.field && item?.value)
                          ?.reduce((acc, cur) => {
                              if (cur?.field && cur?.value) {
                                  acc[cur?.field] = cur?.value;
                              }
                              return acc;
                          }, {})
                    : isBodyChecked && body?.data
                    ? JSON.parse(data?.body?.data)
                    : null;

                let authHeaderValues;
                if (isEditMode && auth_header?.values) {
                    const changedValues = [];
                    Array.from(touchedAuthValueIndices)
                        .sort((a, b) => a - b)
                        .forEach((index) => {
                            const value =
                                auth_header.values[index as number]?.field;
                            if (value && value.trim()) {
                                changedValues.push(value);
                            }
                        });
                    authHeaderValues =
                        changedValues.length > 0 ? changedValues : null;
                } else {
                    authHeaderValues = auth_header?.values
                        ?.map((item) => item?.field)
                        .filter((item) => !!item);
                }

                const mutatedData = {
                    ...data,
                    ...(auth_header && isAuthHeaderChecked
                        ? {
                              auth_header: {
                                  key: auth_header?.key,
                                  type: auth_header?.type,
                                  ...(authHeaderValues?.length
                                      ? {
                                            values: authHeaderValues,
                                        }
                                      : {}),
                              },
                          }
                        : {}),
                    ...(body && isBodyChecked
                        ? {
                              body: {
                                  ...body,
                                  data: bodyData,
                              },
                          }
                        : {}),
                };
                if (!isAuthHeaderChecked) delete mutatedData?.auth_header;
                if (!isBodyChecked) delete mutatedData?.body;
                if (!auth_header?.type) delete auth_header?.type;
                if (!auth_header?.key) delete auth_header?.type;
                if (
                    !auth_header?.key &&
                    !auth_header?.type &&
                    !authHeaderValues?.length
                )
                    delete mutatedData?.auth_header;

                // Handle body data validation differently for JSON vs FORM_PARAMS
                if (isBodyChecked && body?.type) {
                    if (body.type === "JSON") {
                        // For JSON, check if data string is empty
                        if (!body?.data || body.data.trim() === "") {
                            delete mutatedData?.body?.data;
                        }
                    } else {
                        // For FORM_PARAMS, check if object has keys
                        if (!bodyData || Object.keys(bodyData).length === 0) {
                            delete mutatedData?.body?.data;
                        }
                    }
                } else {
                    delete mutatedData?.body;
                }
                if (isEditMode && mutatedData.body?.data) {
                    const filteredData = {};
                    Object.entries(mutatedData.body.data).forEach(
                        ([key, value]) => {
                            if (value !== "••••••") {
                                filteredData[key] = value;
                            }
                        },
                    );
                    if (Object.keys(filteredData).length > 0) {
                        mutatedData.body.data = filteredData;
                    } else {
                        delete mutatedData.body;
                    }
                }

                try {
                    const response = isEditMode
                        ? await putUpdateAuthService(
                              authServiceDetails?.id,
                              mutatedData,
                          )
                        : await postCreateAuthService(mutatedData);

                    if (response.status && !response?.data?.errors) {
                        SwalAlertComp({
                            title: "Success",
                            message: isEditMode
                                ? "Auth Service updated successfully"
                                : "Auth Service created successfully",
                            icon: "success",
                        });
                        onCreateSuccess(response?.data?.id);
                        onClose();
                    } else {
                        !response?.data?.errors &&
                            SwalAlertComp({
                                title: "Failed",
                                message: response?.message ?? "",
                                icon: "error",
                            });
                        response?.data?.errors &&
                            setTestResult(response?.data?.test_result);
                        response?.data?.errors &&
                            SwalAlertComp({
                                title: "Test Failed",
                                message: response?.data?.errors?.join(", "),
                                icon: "error",
                            });
                    }
                } catch (error) {}
                setIsLoadingSubmit(false);
            } catch (error) {
                handleSubmitErrors(error);
                setIsLoadingSubmit(false);
            }
        },
        [
            isAuthHeaderChecked,
            isBodyChecked,
            setIsLoadingSubmit,
            onCreateSuccess,
            onClose,
            isEditMode,
            authServiceDetails?.id,
            touchedAuthValueIndices,
        ],
    );

    const onTestButtonSubmitHandler = useCallback(async () => {
        const data = hookForm.watch() as any;

        setIsLoadingTest(true);

        const auth_header = data?.auth_header;
        const authHeaderValues = auth_header?.values
            ?.map((item) => item?.field)
            .filter((item) => !!item);
        const body = data?.body;

        try {
            const bodyData = Array.isArray(body?.data)
                ? data?.body?.data
                      ?.filter((item) => item?.field && item?.value)
                      ?.reduce((acc, cur) => {
                          if (cur?.field && cur?.value) {
                              acc[cur?.field] = cur?.value;
                          }
                          return acc;
                      }, {})
                : isBodyChecked && body?.data
                ? JSON.parse(data?.body?.data)
                : null;

            const mutatedData = {
                ...data,
                ...(auth_header && isAuthHeaderChecked
                    ? {
                          auth_header: {
                              key: auth_header?.key,
                              type: auth_header?.type,
                              ...(authHeaderValues?.length
                                  ? {
                                        values: authHeaderValues,
                                    }
                                  : {}),
                          },
                      }
                    : {}),
                ...(body && isBodyChecked
                    ? {
                          body: {
                              ...body,
                              data: bodyData,
                          },
                      }
                    : {}),
            };
            if (!isAuthHeaderChecked) delete mutatedData?.auth_header;
            if (!isBodyChecked) delete mutatedData?.body;
            if (!auth_header?.type) delete auth_header?.type;
            if (!auth_header?.key) delete auth_header?.key;
            if (
                !auth_header?.key &&
                !auth_header?.type &&
                !auth_header?.values?.every((value) => !!value)
            )
                delete mutatedData?.auth_header;

            // Handle body data validation differently for JSON vs FORM_PARAMS
            if (isBodyChecked && body?.type) {
                if (body.type === "JSON") {
                    // For JSON, check if data string is empty
                    if (!body?.data || body.data.trim() === "") {
                        delete mutatedData?.body?.data;
                    }
                } else {
                    // For FORM_PARAMS, check if object has keys
                    if (!bodyData || Object.keys(bodyData).length === 0) {
                        delete mutatedData?.body?.data;
                    }
                }
            } else {
                delete mutatedData?.body;
            }
            try {
                const response = await postTestAuthService(mutatedData);

                if (response.status) setTestResult(response?.data);
                else {
                    SwalAlertComp({
                        title: "Failed",
                        message: response?.message ?? "",
                        icon: "error",
                    });
                }
            } catch (error) {}
            setIsLoadingTest(false);
        } catch (error) {
            handleSubmitErrors(error);
        }
        setIsLoadingTest(false);
    }, [
        hookForm,
        setIsLoadingTest,
        setTestResult,
        isAuthHeaderChecked,
        isBodyChecked,
    ]);

    return (
        <>
            <Modal
                className="new-ui-modal create-modal"
                show={true}
                onHide={onClose}
            >
                {isLoadingData ? (
                    <div style={{ padding: "50px", textAlign: "center" }}>
                        <span>Loading...</span>
                    </div>
                ) : (
                    <FormProvider {...hookForm}>
                        <Header isEditMode={isEditMode} />

                        <Content
                            testResult={testResult}
                            setIsBodyChecked={setIsBodyChecked}
                            isAuthHeaderChecked={isAuthHeaderChecked}
                            isBodyChecked={isBodyChecked}
                            setIsAuthHeaderChecked={setIsAuthHeaderChecked}
                            originalAuthValues={originalAuthValues.current}
                            onAuthValueChange={handleAuthValueChange}
                            onResetTouchedIndices={handleResetTouchedIndices}
                            isEditMode={isEditMode}
                        />

                        <Footer
                            handleClose={onClose}
                            onSubmitHandler={onSubmitHandler}
                            onTestButtonSubmitHandler={
                                onTestButtonSubmitHandler
                            }
                            isLoadingSubmit={isLoadingSubmit}
                            isLoadingTest={isLoadingTest}
                            isEditMode={isEditMode}
                        />
                    </FormProvider>
                )}
            </Modal>
        </>
    );
};

export default CreateAuthServiceModal;
